import { number, fileSize } from "./purchase-order-presentation";
import { remainingQuantity } from "../domain/models";
import { Upload, Paperclip, X } from "lucide-react";
import type { PurchaseOrdersViewState } from "./purchase-order-view-state";

type Props = Pick<PurchaseOrdersViewState,
  | "expectedQuantity"
  | "setExpectedQuantity"
  | "quantity"
  | "setQuantity"
  | "returnQuantity"
  | "setReturnQuantity"
  | "deliveredAt"
  | "setDeliveredAt"
  | "deliveryNoteNo"
  | "setDeliveryNoteNo"
  | "returnDisposition"
  | "setReturnDisposition"
  | "returnReason"
  | "setReturnReason"
  | "deliveryDocument"
  | "chooseDeliveryDocument"
  | "setDeliveryDocument"
  | "note"
  | "setNote"> & { dialog: Extract<NonNullable<PurchaseOrdersViewState["dialog"]>, { kind: "receive"; }>; };

export function PurchaseOrderReceiveFields({
  dialog,
  expectedQuantity,
  setExpectedQuantity,
  quantity,
  setQuantity,
  returnQuantity,
  setReturnQuantity,
  deliveredAt,
  setDeliveredAt,
  deliveryNoteNo,
  setDeliveryNoteNo,
  returnDisposition,
  setReturnDisposition,
  returnReason,
  setReturnReason,
  deliveryDocument,
  chooseDeliveryDocument,
  setDeliveryDocument,
  note,
  setNote,
}: Props) {
  return (<>
    <>
      <div className="purchase-order-decision">
        <span>{dialog.order.orderNo}</span>
        <strong>{dialog.order.supplierName}</strong>
        <p>
          Kalan miktar: {number(remainingQuantity(dialog.order))}{" "}
          {dialog.order.unit}
        </p>
      </div>
      <div className="purchase-order-form">
        <label>
          Bu teslimatta planlanan miktar
          <input
            type="number"
            min="0.001"
            max={remainingQuantity(dialog.order)}
            step="0.001"
            value={expectedQuantity}
            onChange={(event) =>
              setExpectedQuantity(event.target.value)
            }
          />
        </label>
        <label>
          Fiilen teslim edilen miktar
          <input
            type="number"
            min="0.001"
            max={expectedQuantity || remainingQuantity(dialog.order)}
            step="0.001"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </label>
        <label>
          İade edilen miktar
          <input
            type="number"
            min="0"
            max={quantity || 0}
            step="0.001"
            value={returnQuantity}
            onChange={(event) =>
              setReturnQuantity(event.target.value)
            }
          />
        </label>
        <label>
          Teslim tarihi
          <input
            type="date"
            value={deliveredAt}
            onChange={(event) => setDeliveredAt(event.target.value)}
          />
        </label>
        <label>
          İrsaliye numarası
          <input
            value={deliveryNoteNo}
            maxLength={80}
            onChange={(event) =>
              setDeliveryNoteNo(event.target.value)
            }
            placeholder="Örn. IRS-2026-00124"
          />
        </label>
        {Number(returnQuantity) > 0 ? (
          <>
            <label>
              İade sonrası işlem
              <select
                value={returnDisposition}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "REPLACE" || value === "DEDUCT_FROM_ORDER") setReturnDisposition(value);
                }}
              >
                <option value="REPLACE">
                  Yenisi tedarikçiden beklensin
                </option>
                <option value="DEDUCT_FROM_ORDER">
                  Sipariş miktarından düşülsün
                </option>
              </select>
            </label>
            <label className="form-field-wide">
              İade gerekçesi
              <input
                value={returnReason}
                onChange={(event) =>
                  setReturnReason(event.target.value)
                }
                placeholder="İadenin nedenini yazın"
              />
            </label>
          </>
        ) : null}
        <div
          className="purchase-order-receipt-preview form-field-wide"
          role="status"
        >
          <strong>Kayıt sonrası miktar özeti</strong>
          <dl>
            <div>
              <dt>Önceki kalan</dt>
              <dd>
                {number(remainingQuantity(dialog.order))}{" "}
                {dialog.order.unit}
              </dd>
            </div>
            <div>
              <dt>Kabul edilecek</dt>
              <dd>
                {number(
                  Math.max(
                    0,
                    Number(quantity || 0) -
                    Number(returnQuantity || 0),
                  ),
                )}{" "}
                {dialog.order.unit}
              </dd>
            </div>
            <div>
              <dt>
                {returnDisposition === "DEDUCT_FROM_ORDER"
                  ? "Siparişten düşülecek"
                  : "İkame beklenecek"}
              </dt>
              <dd>
                {number(Number(returnQuantity || 0))}{" "}
                {dialog.order.unit}
              </dd>
            </div>
            <div>
              <dt>Yeni kalan</dt>
              <dd>
                {number(
                  Math.max(
                    0,
                    remainingQuantity(dialog.order) -
                    Math.max(
                      0,
                      Number(quantity || 0) -
                      Number(returnQuantity || 0),
                    ) -
                    (returnDisposition === "DEDUCT_FROM_ORDER"
                      ? Number(returnQuantity || 0)
                      : 0),
                  ),
                )}{" "}
                {dialog.order.unit}
              </dd>
            </div>
          </dl>
          <span>
            {Math.max(
              0,
              remainingQuantity(dialog.order) -
              Math.max(
                0,
                Number(quantity || 0) - Number(returnQuantity || 0),
              ) -
              (returnDisposition === "DEDUCT_FROM_ORDER"
                ? Number(returnQuantity || 0)
                : 0),
            ) === 0
              ? "Fiziksel teslimat tamamlanacak; süreç kalite kontrolü tamamlanana kadar sonuçlanmayacak."
              : "Sipariş kısmi teslimatta kalacak; eksik veya ikame miktarı sonraki teslimatta izlenecek."}
          </span>
        </div>
        <div className="purchase-order-document-field form-field-wide">
          <span>İrsaliye belgesi</span>
          <label className="purchase-order-document-upload">
            <Upload size={18} />
            <span>
              {deliveryDocument
                ? "Belgeyi değiştir"
                : "PDF, PNG veya JPEG seç"}
            </span>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(event) => {
                chooseDeliveryDocument(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
            />
          </label>
          {deliveryDocument ? (
            <div className="purchase-order-selected-document">
              <div>
                <Paperclip size={16} />
                <span>
                  <strong>{deliveryDocument.name}</strong>
                  <small>{fileSize(deliveryDocument.size)}</small>
                </span>
              </div>
              <button
                type="button"
                aria-label="Seçilen irsaliye belgesini kaldır"
                onClick={() => setDeliveryDocument(null)}
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <small>
              Belge, teslimat kaydıyla birlikte güvenlik
              doğrulamasından geçirilir.
            </small>
          )}
        </div>
        <label className="form-field-wide">
          Teslimat notu
          <textarea
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="İrsaliye veya operasyon notu"
          />
        </label>
      </div>
    </>
  </>);
}
